import moment from 'moment'
import { useEffect, useState } from 'react'
import { HOURS_COEFF } from '../constants'
import { TextField } from '@mui/material'

export const timezoneOffsets = {
    'Pacific Time (US & Canada)': -8,
    'Central Time (US & Canada)': -6,
    'Eastern Time (US & Canada)': -5,
    'Asia/Tokyo': 9,
    Japan: 9,
    Hawaii: -10,
    UTC: 0
}

/** Max duration for a single day’s shift entry (minutes). Separate from the Labor Standards Act’s ordinary 8h/day; longer hours can occur under Article 36 agreements, but this caps extreme values in the app. */
export const MAX_LEGAL_SHIFT_DURATION_MINUTES = 16 * 60

export const MAX_LEGAL_SHIFT_DURATION_HOURS = MAX_LEGAL_SHIFT_DURATION_MINUTES / 60

/** 日次シフト表で確定する 1 セルあたりの時間（15 分） */
export const SHIFT_CALENDAR_SLOT_MINUTES = 15
export const SHIFT_CALENDAR_SLOT_HOURS = SHIFT_CALENDAR_SLOT_MINUTES / 60

/**
 * Validates modal shift times (HH:mm) against store hours and max shift duration.
 * @param {string} modalStartTime
 * @param {string} modalEndTime
 * @param {object} [storeHour] - operation_hours
 * @param {Date|string} [modalDate] - calendar day for the shift start
 */
export function validateShiftModalTimes(modalStartTime, modalEndTime, storeHour, modalDate) {
    const startGridInvalid = Number(modalStartTime.split(':')[1]) % 15 > 0
    const endMinutesInvalid = Number(modalEndTime.split(':')[1]) % 15 > 0
    const [shour, sminute] = modalStartTime.split(':').map(Number)
    const [ehour, eminute] = modalEndTime.split(':').map(Number)
    const openHour = storeHour ? parseInt(storeHour.open_time?.split(':')[0] || 0, 10) : 0
    const closeHour = storeHour ? parseInt(storeHour.close_time?.split(':')[0] || 24, 10) : 24
    const closeMinute = storeHour ? parseInt(storeHour.close_time?.split(':')[1] || 0, 10) : 0
    const crossesMidnight = !!storeHour && (storeHour.next_day_end || closeHour < openHour)
    const endIsNextDay = crossesMidnight && ehour < openHour
    const startMinutesTotal = shour * 60 + sminute
    const endMinutesTotal = endIsNextDay ? (ehour + 24) * 60 + eminute : ehour * 60 + eminute
    const closeMinutesTotal = closeHour * 60 + closeMinute
    const endBeforeStart = !endMinutesInvalid && endMinutesTotal <= startMinutesTotal
    const endExceedsClose =
        !endMinutesInvalid &&
        storeHour &&
        (endIsNextDay
            ? ehour * 60 + eminute > closeMinutesTotal
            : crossesMidnight
            ? false
            : endMinutesTotal > closeMinutesTotal)

    // Start-time vs business hours does not depend on end-minute validity (end can be off-grid while start is still checked)
    let startOutsideBusinessHours = false
    if (storeHour) {
        const openMinute = parseInt(storeHour.open_time?.split(':')[1] || 0, 10)
        const openMinutesTotal = openHour * 60 + openMinute
        if (!crossesMidnight) {
            startOutsideBusinessHours = startMinutesTotal < openMinutesTotal || startMinutesTotal >= closeMinutesTotal
        } else if (!endIsNextDay) {
            startOutsideBusinessHours = startMinutesTotal < openMinutesTotal
        }
    }

    let durationExceedsLimit = false
    let durationMinutes = 0
    if (modalDate && !endMinutesInvalid && !endBeforeStart) {
        const mStart = moment(modalDate).set({ hour: shour, minute: sminute, second: 0, milliseconds: 0 })
        const mEnd = endIsNextDay
            ? moment(modalDate).set({ hour: ehour, minute: eminute, second: 0, milliseconds: 0 }).add(1, 'day')
            : moment(modalDate).set({ hour: ehour, minute: eminute, second: 0, milliseconds: 0 })
        durationMinutes = mEnd.diff(mStart, 'minutes')
        durationExceedsLimit = durationMinutes > MAX_LEGAL_SHIFT_DURATION_MINUTES
    }

    const invalid =
        startGridInvalid ||
        startOutsideBusinessHours ||
        endMinutesInvalid ||
        endBeforeStart ||
        endExceedsClose ||
        durationExceedsLimit

    return {
        startGridInvalid,
        startOutsideBusinessHours,
        endMinutesInvalid,
        endBeforeStart,
        endExceedsClose,
        durationExceedsLimit,
        durationMinutes,
        invalid
    }
}

export const stitchShifts = (shifts, tzOffset = 0, skipOffset = false) => {
    let submittedShifts = [...shifts]
    // sort by time
    submittedShifts = submittedShifts.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    // group by employment id
    let groupedShifts = submittedShifts.reduce((acc, shift) => {
        acc[shift.employment_id] = acc[shift.employment_id] || []
        acc[shift.employment_id].push(shift)
        return acc
    }, {})

    // iterate through each employment and stitch together the shifts
    const allShifts = []
    for (const [, employeeShifts] of Object.entries(groupedShifts)) {
        let start = null
        let end = null
        let prevShift = null
        const stitchedShifts = []
        const offsetShifts = employeeShifts.map(shift => {
            const offsetStart = shift.startDate.getTimezoneOffset() / 60 + tzOffset
            const offsetEnd = shift.endDate.getTimezoneOffset() / 60 + tzOffset

            const offsetStartDate = new Date(shift.startDate) // avoiding editing the original shift in state
            const offsetEndDate = new Date(shift.endDate)

            if (!skipOffset) {
                offsetStartDate.setHours(shift.startDate.getHours() - offsetStart)
                offsetEndDate.setHours(shift.endDate.getHours() - offsetEnd)
            }

            return { ...shift, startDate: offsetStartDate, endDate: offsetEndDate }
        })
        offsetShifts.forEach((shift, index) => {
            if (start === null) {
                start = shift.startDate
                end = shift.endDate
            } else if (shift.startDate.getTime() === prevShift.endDate.getTime() && shift.status === prevShift.status) {
                end = shift.endDate
            } else {
                stitchedShifts.push({
                    start_time: start,
                    end_time: end,
                    employment_id: shift.employment_id,
                    status: prevShift.status,
                    isFusoku: shift.isFusoku,
                    row_number: shift.row_number
                })
                start = shift.startDate
                end = shift.endDate
            }

            if (index === employeeShifts.length - 1) {
                end = shift.endDate
                stitchedShifts.push({
                    start_time: start,
                    end_time: end,
                    employment_id: shift.employment_id,
                    status: 'confirmed',
                    isFusoku: shift.isFusoku,
                    row_number: shift.row_number
                })
            }
            prevShift = shift
        })
        allShifts.push(...stitchedShifts)
    }
    return allShifts
}

export const splitShiftsIntoBlocks = shifts => {
    const splitShifts = []
    shifts.forEach(shift => {
        const startHour = shift.startDate.getHours()
        let endHour = shift.endDate.getHours()
        const startMinutes = shift.startDate.getMinutes()
        const endMinutes = shift.endDate.getMinutes()
        let breakStartHour = shift.breakStartDate ? shift.breakStartDate.getHours() : null
        const breakStartMinutes = shift.breakStartDate ? shift.breakStartDate.getMinutes() : null
        let breakEndHour = shift.breakEndDate ? shift.breakEndDate.getHours() : null
        const breakEndMinutes = shift.breakEndDate ? shift.breakEndDate.getMinutes() : null
        if (shift.endDate.getDate() !== shift.startDate.getDate()) {
            endHour += 24
        }
        if (breakEndHour < startHour) {
            breakEndHour += 24
        }
        if (breakStartHour < startHour) {
            breakStartHour += 24
        }

        for (let i = startHour * 60 + startMinutes; i < endHour * 60 + endMinutes; i += 15) {
            if (
                breakStartHour &&
                breakEndHour &&
                i >= breakStartHour * 60 + breakStartMinutes &&
                i < breakEndHour * 60 + breakEndMinutes
            ) {
                continue
            } else {
                const currentHour = Math.floor(i / 60)
                const currentMinutes = i % 60
                const nextHour = Math.floor((i + 15) / 60)
                const nextMinutes = (i + 15) % 60
                const newShift = {
                    ...shift,
                    originalStatus: shift.status,
                    id: `${shift.id} ${i}`,
                    startDate: new Date(new Date(shift.startDate).setHours(currentHour, currentMinutes)),
                    endDate: new Date(new Date(shift.startDate).setHours(nextHour, nextMinutes))
                }
                delete newShift.created_at
                delete newShift.updated_at
                splitShifts.push(newShift)
            }
        }
    })
    return splitShifts
}

export const parseTimeBlock = (timeblock, tzOffset = 0) => {
    const dates = timeblock.replaceAll(' UTC', '').split('..')
    const [startDate, endDate] = dates.map(e => moment.utc(e).toDate())
    const startDateTokyoOffset = startDate.getTimezoneOffset() / 60 + tzOffset
    const endDateTokyoOffset = endDate.getTimezoneOffset() / 60 + tzOffset
    startDate.setHours(startDate.getHours() + startDateTokyoOffset)
    endDate.setHours(endDate.getHours() + endDateTokyoOffset)

    return [startDate, endDate]
}

export const getTotalShiftHours = shifts => {
    return shifts.reduce((acc, shift) => {
        if (shift.end_time < shift.start_time) {
            return acc
        }
        return acc + (shift.end_time - shift.start_time) / HOURS_COEFF
    }, 0)
}

export const getAllXDays = (yearMonth = moment().format('YYYY-MM'), dayOfWeek) => {
    // yearMonth is string in format YYYY-MM
    // dayOfWeek is integer 0-6, sunday to saturday
    const days = []
    const date = moment(yearMonth, 'YYYY-MM').startOf('month')
    const month = date.month()
    while (date.month() === month) {
        if (date.day() === dayOfWeek) {
            days.push(date.date())
            date.add(7, 'day')
        } else {
            date.add(1, 'day')
        }
    }
    return days
}

export const getDays = yearMonth => {
    const days = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] }
    const date = moment(yearMonth, 'YYYY-MM').startOf('month')
    const month = date.month()
    while (date.month() === month) {
        days[date.day()].push(date.date())
        date.add(1, 'day')
    }
    return days
}

/**
 * Safari's native time control crashes the renderer — the tab goes blank and
 * reloads with "A problem repeatedly occurred" — when the user selects the
 * displayed time. Chrome's picker does not. MUI's input styles (display:block,
 * a fixed em height, and autofill keyframes that animate `display`) hit that
 * WebKit bug on the fixed-shift screen. Desktop Safari and every iOS browser
 * therefore edit these times as plain HH:mm text.
 */
export const browserCrashesOnNativeTimeSelection = () => {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent || ''
    const isIOS = /iP(hone|ad|od)/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    const isDesktopSafari = /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Android/i.test(ua)
    return isIOS || isDesktopSafari
}

/** True for a complete, in-range HH:mm value a native time input could produce. */
export const isValidHhMm = raw => {
    const match = String(raw || '').match(/^(\d{2}):(\d{2})$/)
    if (!match) return false
    const hours = Number(match[1])
    const minutes = Number(match[2])
    return hours <= 23 && minutes <= 59
}

/** Keep an in-progress edit as HH:mm while the user types digits. */
export const normalizeTypedTime = raw => {
    if (raw == null) return ''
    const cleaned = String(raw).replace(/[^\d:]/g, '')
    if (cleaned === '') return ''
    if (!cleaned.includes(':')) {
        const digits = cleaned.slice(0, 4)
        if (digits.length <= 2) return digits
        return `${digits.slice(0, 2)}:${digits.slice(2)}`
    }
    const colon = cleaned.indexOf(':')
    const hours = cleaned.slice(0, colon).slice(0, 2)
    const minutes = cleaned
        .slice(colon + 1)
        .replace(/:/g, '')
        .slice(0, 2)
    if (minutes === '' && cleaned.endsWith(':')) return `${hours}:`
    return minutes.length ? `${hours}:${minutes}` : hours
}

/**
 * Finish a typed time as zero-padded HH:mm, or null when incomplete / out of range.
 * Callers must not persist null — revert to the last good value instead.
 */
export const commitTypedTime = raw => {
    const normalized = normalizeTypedTime(raw)
    const match = normalized.match(/^(\d{1,2}):(\d{2})$/)
    if (!match) return null
    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 23 || minutes > 59) return null
    return `${String(hours).padStart(2, '0')}:${match[2]}`
}

/** Reject anything a native time picker would not allow, plus off the 15-minute grid. */
export const isInvalidScheduleTime = time => {
    if (!isValidHhMm(time)) return true
    return Number(time.split(':')[1]) % 15 !== 0
}

const emitTimeValue = (onChange, event, value) => {
    if (!onChange) return
    if (event && value === event.target.value) {
        onChange(event)
        return
    }
    onChange({ target: { value } })
}

const TimeField = ({ id, label, value, onChange, onFocus, error, helperText, disabled, onKeyPress, testId, sx }) => {
    const textInput = browserCrashesOnNativeTimeSelection()
    // Incomplete digits stay local; a complete HH:mm is pushed up immediately so
    // Save can enable while focused. Rows use stable keys, so that does not remount.
    const [draft, setDraft] = useState(value ?? '')
    const [focused, setFocused] = useState(false)

    useEffect(() => {
        if (!focused) setDraft(value ?? '')
    }, [value, focused])

    if (!textInput) {
        return (
            <TextField
                type="time"
                id={id}
                label={label}
                sx={sx}
                value={value}
                onChange={onChange}
                onFocus={onFocus}
                error={error}
                helperText={helperText}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 900, 'data-testid': testId }}
                disabled={disabled}
                onKeyPress={onKeyPress}
            />
        )
    }

    const pushIfComplete = (raw, event) => {
        const next = commitTypedTime(raw)
        if (next == null) return null
        if (next !== value) {
            emitTimeValue(onChange, event, next)
        }
        return next
    }

    return (
        <TextField
            type="text"
            id={id}
            label={label}
            sx={sx}
            value={focused ? draft : value ?? ''}
            onChange={event => {
                const nextDraft = normalizeTypedTime(event.target.value)
                setDraft(nextDraft)
                pushIfComplete(nextDraft, event)
            }}
            onBlur={event => {
                setFocused(false)
                const next = pushIfComplete(event.target.value, event)
                if (next == null) {
                    // Invalid / incomplete: do not push garbage to parent.
                    setDraft(value ?? '')
                    return
                }
                setDraft(next)
            }}
            onFocus={event => {
                setFocused(true)
                setDraft(value ?? '')
                onFocus?.(event)
            }}
            error={error}
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
            inputProps={{
                inputMode: 'numeric',
                autoComplete: 'off',
                spellCheck: false,
                placeholder: '09:00',
                pattern: '[0-9]{2}:[0-9]{2}',
                'data-testid': testId
            }}
            disabled={disabled}
            onKeyPress={onKeyPress}
        />
    )
}

export const InputTime = ({
    startValue,
    endValue,
    onfocus,
    startOnChange,
    endOnChange,
    sx,
    startError,
    endError,
    startHelperText: startHelperTextProp,
    endHelperText: endHelperTextProp,
    disabled = false,
    handleKeyPress = () => {},
    startTestId,
    endTestId,
    addBreak = false,
    breakStartTime = '',
    breakEndTime = '',
    setBreakStartTime = () => {},
    setBreakEndTime = () => {},
    breakStartError = false,
    breakEndError = false
}) => {
    const startHelperText = startError ? startHelperTextProp || '15分単位でのスケジュールが可能です。' : null
    const endHelperText = endError ? endHelperTextProp || '15分単位でのスケジュールが可能です。' : null
    const breakStartHelperText = breakStartError ? '15分単位でのスケジュールが可能です。' : null
    const breakEndHelperText = breakEndError ? '15分単位でのスケジュールが可能です。' : null
    return (
        <>
            <TimeField
                id="start-time"
                label="開始時間"
                sx={sx}
                value={startValue}
                onChange={startOnChange}
                onFocus={onfocus}
                error={startError}
                helperText={startHelperText}
                testId={startTestId}
                disabled={disabled}
                onKeyPress={handleKeyPress}
            />
            <TimeField
                id="end-time"
                label="終了時間"
                sx={sx}
                value={endValue}
                onChange={endOnChange}
                onFocus={onfocus}
                error={endError}
                helperText={endHelperText}
                testId={endTestId}
                disabled={disabled}
                onKeyPress={handleKeyPress}
            />
            {addBreak && (
                <>
                    <TimeField
                        id="break-start-time"
                        label="休憩開始時間"
                        sx={sx}
                        value={breakStartTime}
                        onChange={setBreakStartTime}
                        onFocus={onfocus}
                        error={breakStartError}
                        helperText={breakStartHelperText}
                        disabled={disabled}
                        onKeyPress={handleKeyPress}
                    />
                    <TimeField
                        id="break-end-time"
                        label="休憩終了時間"
                        sx={sx}
                        value={breakEndTime}
                        onChange={setBreakEndTime}
                        onFocus={onfocus}
                        error={breakEndError}
                        helperText={breakEndHelperText}
                        disabled={disabled}
                        onKeyPress={handleKeyPress}
                    />
                </>
            )}
        </>
    )
}
